import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, CalendarDays, List } from 'lucide-react';
import { fetchAvailableSlots, bookSlot } from '../../api/forms';
import { formatAgeRange } from '../../lib/ageGroups';
import { formatDayLabel } from '../../lib/calendar';
import BookingCalendar from './BookingCalendar';
import type { AvailableSlot, FormOption } from '../../types/forms';

interface Props {
  leadId: string;
  option: FormOption;
  // Das Probetraining ist für ein Kind („Für mein Kind").
  forChild?: boolean;
  onBooked: (info: { replacedCount: number }) => void;
}

type ViewMode = 'calendar' | 'list';

const hhmm = (t: string) => t.slice(0, 5);

export default function PathBooking({ leadId, option, forChild = false, onBooked }: Props) {
  // Pick a slot, then seal the booking directly against the CRM. The welcome
  // video is no longer shown in the form — it is sent by mail/SMS after the
  // appointment is booked (see CRM trial-sequence, touchpoint "Willkommen + Video").
  const [selected, setSelected] = useState<AvailableSlot | null>(null);
  // Default to the month overview (less overwhelming than the long list); users
  // can switch to the classic list any time.
  const [view, setView] = useState<ViewMode>('calendar');
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sealed = useRef(false);

  const { data: slots, isLoading } = useQuery({
    queryKey: ['slots', option.school_id, option.course_id],
    queryFn: () => fetchAvailableSlots(option.school_id, option.course_id),
  });

  // Grouped by day for the list view.
  const grouped = useMemo(() => {
    const map = new Map<string, AvailableSlot[]>();
    for (const s of slots ?? []) {
      if (!map.has(s.booking_date)) map.set(s.booking_date, []);
      map.get(s.booking_date)!.push(s);
    }
    return Array.from(map.entries());
  }, [slots]);

  const hasHolidaySlots = grouped.some(([, ds]) => ds.some((s) => s.requires_confirmation));

  // Kurse mit Altersbereich nennen („Jiu Kids (9–11 Jahre)"): Wer den Termin für
  // ein Kind bucht — oder einen Kinderkurs —, sieht noch einmal, für welches Alter
  // der Kurs gedacht ist. Erwachsene brauchen das bei „Jiu Erwachsene" nicht.
  const ageText =
    forChild || option.for_adults === false ? formatAgeRange(option.age_min, option.age_max) : '';

  // Commits the chosen slot to the CRM. Guarded so a double click can't double-book.
  const sealBooking = async () => {
    if (!selected || sealed.current) return;
    sealed.current = true;
    setError(null);
    setBooking(true);
    try {
      const res = await bookSlot(leadId, selected.schedule_id, selected.booking_date);
      onBooked({ replacedCount: res.replaced_count ?? 0 });
    } catch (e) {
      sealed.current = false;
      setError((e as Error).message);
      setBooking(false);
    }
  };

  return (
    <div className="rf-card">
      {isLoading && (
        <div className="rf-center"><Loader2 className="animate-spin" /> Lade freie Termine …</div>
      )}

      {!isLoading && grouped.length === 0 && (
        <div className="rf-alert rf-alert--info">
          Aktuell sind online keine freien Probetraining-Termine verfügbar. Wähle bitte den
          Rückruf, dann finden wir gemeinsam einen Termin.
        </div>
      )}

      {!isLoading && grouped.length > 0 && (
        <>
          <div className="rf-bookhead">
            <p className="rf-muted" style={{ margin: 0 }}>
              Wähle einen freien Termin für <strong>{option.course_name}</strong>
              {ageText && ` (${ageText})`} in <strong>{option.school_name}</strong>:
            </p>
            <div className="rf-viewtoggle" role="group" aria-label="Darstellung der Termine">
              <button
                type="button" aria-pressed={view === 'calendar'}
                onClick={() => setView('calendar')}
              >
                <CalendarDays size={15} /> Kalender
              </button>
              <button
                type="button" aria-pressed={view === 'list'}
                onClick={() => setView('list')}
              >
                <List size={15} /> Liste
              </button>
            </div>
          </div>

          {hasHolidaySlots && (
            <div className="rf-alert rf-alert--info" style={{ marginBottom: '1rem' }}>
              Termine nach den Sommerferien kannst du schon jetzt buchen. Für die als{' '}
              <strong>„Termin nach den Ferien"</strong> markierten Termine bekommst du rund 14 Tage
              vorher eine E-Mail und bestätigst noch einmal – sonst wird der Platz frei.
            </div>
          )}

          {view === 'calendar' ? (
            <BookingCalendar slots={slots ?? []} selected={selected} onSelect={setSelected} />
          ) : (
            grouped.map(([date, daySlots]) => (
              <div className="rf-daygroup" key={date}>
                <div className="rf-daytitle">{formatDayLabel(date)}</div>
                <div className="rf-slots">
                  {daySlots.map((s) => {
                    const isSel = selected?.schedule_id === s.schedule_id && selected?.booking_date === s.booking_date;
                    return (
                      <button
                        key={s.schedule_id + s.booking_date}
                        className={`rf-slot ${isSel ? 'rf-slot--selected' : ''}`}
                        disabled={s.free <= 0}
                        onClick={() => setSelected(s)}
                      >
                        <span className="rf-slot__time">{hhmm(s.start_time)}–{hhmm(s.end_time)}</span>
                        <span className="rf-slot__free">
                          {s.free > 0 ? `${s.free} von ${s.trial_capacity} frei` : 'ausgebucht'}
                          {s.room_name ? ` · ${s.room_name}` : ''}
                          {s.requires_confirmation ? ' · Termin nach den Ferien' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          {selected?.requires_confirmation && (
            <div className="rf-alert rf-alert--info" style={{ margin: '1rem 0 0' }}>
              Dein Termin liegt noch etwas in der Zukunft (nach den Sommerferien).
              Rund <strong>14 Tage vorher</strong> bekommst du von uns eine E-Mail mit
              der Bitte, den Termin kurz zu bestätigen. Bestätigst du nicht innerhalb
              von <strong>2 Tagen</strong>, geben wir den Platz wieder frei.
            </div>
          )}

          {error && <div className="rf-alert rf-alert--error" style={{ marginTop: '1rem' }}>{error}</div>}

          {selected && (
            <div className="rf-bookbar">
              Gewählt: <strong>{formatDayLabel(selected.booking_date)}</strong>
              {' · '}{hhmm(selected.start_time)}–{hhmm(selected.end_time)}
            </div>
          )}

          <button
            className="btn btn-primary btn-block"
            style={{ marginTop: '1rem' }}
            disabled={!selected || booking}
            onClick={sealBooking}
          >
            {booking ? (
              <><Loader2 className="animate-spin" size={18} /> Dein Termin wird gesichert …</>
            ) : (
              'Termin verbindlich buchen'
            )}
          </button>
        </>
      )}
    </div>
  );
}
